import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';
import type { MediaItem } from '@/types/media';
import { MEDIA_TYPE_LABEL } from '@/types/media';

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (!items.length) return null;
  const current = items[index];

  return (
    <section className="relative h-[620px] w-full overflow-hidden md:h-[660px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={i !== index}
        >
          <img
            src={item.backdrop || item.cover}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 黄色影视`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/30"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-background-50/70"></div>

      <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-center px-4 md:px-6">
        <div key={current.id} className="hs-fade-up max-w-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-fire-fill"></i>
              焦点推荐
            </span>
            <span className="rounded-full border border-background-300/60 bg-background-100/70 px-3 py-1 text-xs text-foreground-700">
              {MEDIA_TYPE_LABEL[current.type]}
            </span>
            <span className="flex items-center gap-1 text-sm font-semibold text-primary-500">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-5xl">
            {current.title}
          </h1>

          <p className="mt-3 text-sm text-foreground-600 md:text-base">
            {current.year} · {current.region} · {current.genres.join(' / ')} · {current.duration}
          </p>

          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {current.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-100/60 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200"
            >
              更多同类
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-8 max-w-lg">
            <SearchBar size="lg" />
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-4 hidden items-center gap-2 md:right-6 md:flex">
        <button
          type="button"
          aria-label="上一部"
          onClick={() => setIndex((prev) => (prev - 1 + items.length) % items.length)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/70 bg-background-100/70 text-foreground-800 backdrop-blur transition-colors hover:bg-primary-500 hover:text-background-50"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => setIndex((prev) => (prev + 1) % items.length)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/70 bg-background-100/70 text-foreground-800 backdrop-blur transition-colors hover:bg-primary-500 hover:text-background-50"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute bottom-8 left-4 flex items-center gap-2 md:left-6">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-400'
            }`}
          />
        ))}
      </div>
    </section>
  );
}